import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { documentService } from "../../services/api/documentService";
import { FileTextIcon } from "lucide-react";
import { formatDate } from "../../utility/formatDate";
import ModalNew from "../../components/Modal/ModalNew";
import PdfViewer from "../../components/elements/PdfViewer";
import { Document } from "../../store/types/document";
import { useAuth } from "../auth/hooks/useAuth";

const Documents: React.FC = () => {
  const { id } = useParams();
  const { authData } = useAuth();
  const organizationId =
    id || authData?.admin?.organization?.id || authData?.admin?.organizationId;

  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [pdfModal, setPdfModal] = useState({
    isOpen: false,
    pdfUrl: "",
    documentType: "",
  });

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!organizationId) {
        console.warn("No organizationId found for fetching documents");
        return;
      }

      setLoadingDocs(true);
      try {
        console.log(`Fetching documents for organization: ${organizationId}`);
        let response;
        if (id) {
          // Super admin view of a specific organization
          response = await documentService.getDocuments(id);
        } else {
          // Organization admin view of their own documents
          // We use the same underlying API as Super Admin, but called as an organization
          response = await documentService.getOrgDocuments(organizationId);
        }

        if (
          response?.data?.documents &&
          Array.isArray(response.data.documents)
        ) {
          setDocuments(response.data.documents);
        }
      } catch (error) {
        // Error handling is done in the service
        console.error("Failed to fetch documents:", error);
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchDocuments();
  }, [id, organizationId]);

  const openPdfModal = (pdfUrl: string, documentType: string) => {
    setPdfModal({ isOpen: true, pdfUrl, documentType });
  };

  const closePdfModal = () => {
    setPdfModal({ isOpen: false, pdfUrl: "", documentType: "" });
  };
  return (
    <div className="w-full h-full">
      {/* Organization Documents Section - Full Width */}
      {documents && documents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-[gray]/20 p-6 mt-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileTextIcon className="w-5 h-5 mr-2" />
              Organization Documents ({documents.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Upload Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {doc?.type}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-gray-900">
                      {formatDate(doc?.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap flex items-center space-x-3">
                      <button
                        onClick={() => openPdfModal(doc.url, doc.type)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                      >
                        View {doc.type}
                      </button>
                      {/* {doc.type === "SLA" && (
                          <button
                            onClick={() => handleApproveSla(organizationId || "", doc.id)}
                            disabled={isApproving === doc.id}
                            className={`font-medium text-sm ${
                              isApproving === doc.id
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-green-600 hover:text-green-800"
                            }`}
                          >
                            {isApproving === doc.id
                              ? "Approving..."
                              : "Approve SLA"}
                          </button>
                        )}
                        {doc.type === "ONBOARDING_FORM" && (
                          <button
                            onClick={() => handleApproveSla(organizationId || "", doc.id)}
                            disabled={isApproving === doc.id}
                            className={`font-medium text-sm ${
                              isApproving === doc.id
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-green-600 hover:text-green-800"
                            }`}
                          >
                            {isApproving === doc.id
                              ? "Approving..."
                              : "Approve Onboarding Form"}
                          </button>
                        )} */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ModalNew
        isOpen={pdfModal.isOpen}
        onClose={closePdfModal}
        title={`${pdfModal.documentType} Document`}
      >
        <div className="p-0">
          <PdfViewer url={pdfModal.pdfUrl} />
        </div>
      </ModalNew>
    </div>
  );
};

export default Documents;
